import type { GetListParams, GetListResult } from "react-admin";
import type { location, Offer } from "../models";
import { apiKey, httpClient, restURL } from "./httpClient";
import { mapbox } from "../config";

export const fetchActive = async ({
    filter,
    pagination = { page: 0, perPage: 12 },
    sort = { field: "id", order: "ASC" },
    signal,
    meta = { headers: {} },
}: GetListParams): Promise<GetListResult<any>> => {
    const p = new URLSearchParams();
    p.append("offset", (pagination.perPage * (pagination.page - 1)).toString());
    p.append("limit", pagination.perPage.toString());
    p.append("order", `${sort.field}.${sort.order.toLocaleLowerCase()}`);

    const { categories, brands, rating, min, max, txt, tags, color } = filter as { color: number[], categories: number[], brands: number[], tags: string[], rating: number, min: number, max: number, txt: string };

	p.append("categories", `{${categories.join(",")}}`);
	p.append("brands", `{${brands.join(",")}}`);
    p.append("colors", `{${color.join(",")}}`);
	p.append("txt", txt ?? "0");


	if (tags.length) {
		p.append("tags", `{${tags.join(",")}}`);
	}
	if (rating) {
		p.append("rating", `gte.${rating}`);
	}
	if (min) {
		p.append("minPrice", `gte.${min}`);
	}
	if (max) {
		p.append("maxPrice", `lte.${max}`);
	}

    const q = decodeURI(p.toString());
    // @ts-expect-error
    const result = await window.shop;
    const diff = Date.now() - (result?.timestamp ?? 0);

    if (q === "offset=0&limit=12&order=name.asc&categories={}&brands={}&colors={}&txt=0" && diff < 5000) {
        return result;
    }
    const response = await httpClient(`${restURL}/rpc/shop?${q}`, {
        method: "GET",
        mode: "same-origin",
        credentials: "include",
        headers: new Headers({ ...meta.headers, apikey: apiKey, Prefer: "count=estimated" }),
        signal,
        priority: "high",
    });
    const data = response.json as Offer[];
    const content = response.headers.get("content-range")?.split("/") ?? [];
    const range = content[0].split("-").map(x => Number.parseInt(x));
    const total = content[1] && content[1] !== "*" ? Number.parseInt(content[1]) : range[1] + 1;
    return { data, total } as GetListResult;
};

export const fetchAddress = async ({
    filter: { address, state, country, city },
}: GetListParams): Promise<GetListResult<any>> => {
    if(!address || address.length < 3) {
        return { data: [], total: 0 }
    }
    let q = address;
    if(city) {
        q = `${q} ${city}`;
    }
    if(state) {
        q = `${q} ${state}`;
    }
    const p = new URLSearchParams({
        access_token: mapbox,
        language: "en",
        types: "street",
        q,
    });
    if (country) {
        p.append("country", country);
    }

    const response = await fetch(
        `https://api.mapbox.com/search/geocode/v6/forward?${p.toString()}`
    ).then(x => x.json());


    const data: location[] = (response.features || []).map((feature: any) => {
        const { name, context, coordinates, mapbox_id } = feature.properties;

        return {
            id: mapbox_id,
            externalId: mapbox_id,
            name: name,
            point: `(${coordinates?.longitude},${coordinates?.latitude})`,
            country: context?.country?.country_code,
            state: context?.region?.name,
            city: context?.district?.name ?? context?.place?.name,
            zip: context?.postcode?.name,
            address: name,
        } as location;
    });
    return { data, total: data.length };
};